import wrapperHOC from "@Utils/wrapperHOC";
import { memo, useEffect, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import type { AppState } from "@Store/index";
import axios from "axios";
import config from "@Config/index";
import FeaturedBlogSection from "@Pages/Blog/BlogHome/components/FeaturedBlogSection/FeaturedBlogSection";
import getBlogImageUrl from "@Pages/Blog/utils/getBlogImageUrl";
import styles from "./SearchFeaturedBlogs.module.scss";

const SearchFeaturedBlogs = () => {
  const { region } = useSelector(
    ({ searchPageData }: AppState) => ({
      region: searchPageData?.region,
    }),
    shallowEqual,
  );

  const [blogs, setBlogs] = useState<any[]>([]);

  const cityName = region?.name || "";
  const regionType = region?.region_type;
  const countryName =
    region?.root_name ||
    region?.ancestors_short?.find((a: any) => a.region_type === "country")?.name ||
    "";

  // Resolve the city-level name — for city type use region.name directly,
  // for sub-city types (locality, establishment, area, etc.) use the parent city from ancestors
  // If no city ancestor found, use region.name as the city (e.g. Dublin is "locality" but IS the city)
  const resolvedCityName = (() => {
    if (regionType === "city") return cityName;
    if (regionType === "country" || regionType === "state") return "";
    // For locality, establishment, area, etc. — find the city ancestor
    const cityAncestor = region?.ancestors_short?.find((a: any) => a.region_type === "city");
    if (cityAncestor?.name) return cityAncestor.name;
    // If parent is a country/state (not a city), use the region name itself as the city
    const parentType = region?.ancestors_short?.find(
      (a: any) => a.name === region?.parent_name,
    )?.region_type;
    if (parentType === "country" || parentType === "state") return cityName;
    return region?.parent_name || cityName;
  })();

  useEffect(() => {
    const fetchBlogs = async () => {
      if (!resolvedCityName && !countryName) return;

      try {
        const baseSelect = `select[id]=true&select[name]=true&select[slug]=true&select[mainImage]=true&select[category]=true&select[readTime]=true&select[date]=true&select[updatedAt]=true`;
        const baseFilter = `where[_status][equals]=published&where[isArchived][equals]=false`;
        const searchName = resolvedCityName;
        const citySlug = searchName ? searchName.toLowerCase().replace(/\s+/g, "-") : "";
        const countrySlug = countryName ? countryName.toLowerCase().replace(/\s+/g, "-") : "";

        // Step 1: Look up city and country IDs in parallel
        const [cityLookup, countryLookup] = await Promise.allSettled([
          citySlug
            ? axios.get(
                `${config.CMS_API_URL}/api/cities?where[slug][equals]=${citySlug}&select[id]=true&limit=1`,
              )
            : Promise.resolve(null),
          countrySlug
            ? axios.get(
                `${config.CMS_API_URL}/api/countries?where[slug][equals]=${countrySlug}&select[id]=true&limit=1`,
              )
            : Promise.resolve(null),
        ]);

        const cmsCityId =
          cityLookup.status === "fulfilled" && cityLookup.value
            ? cityLookup.value.data?.docs?.[0]?.id
            : null;
        const cmsCountryId =
          countryLookup.status === "fulfilled" && countryLookup.value
            ? countryLookup.value.data?.docs?.[0]?.id
            : null;

        // Step 2: Fetch city, country, and latest blogs in parallel (10 each)
        const [cityBlogs, countryBlogs, latestBlogs] = await Promise.allSettled([
          cmsCityId
            ? axios.get(
                `${config.CMS_API_URL}/api/blog-posts?where[city][equals]=${cmsCityId}&${baseSelect}&${baseFilter}&limit=10&sort=-updatedAt`,
              )
            : Promise.resolve(null),
          cmsCountryId
            ? axios.get(
                `${config.CMS_API_URL}/api/blog-posts?where[country][equals]=${cmsCountryId}&${baseSelect}&${baseFilter}&limit=10&sort=-updatedAt`,
              )
            : Promise.resolve(null),
          axios.get(
            `${config.CMS_API_URL}/api/blog-posts?${baseSelect}&${baseFilter}&limit=10&sort=-updatedAt`,
          ),
        ]);

        const cityDocs = (cityBlogs.status === "fulfilled" && cityBlogs.value?.data?.docs) || [];
        const countryDocs =
          (countryBlogs.status === "fulfilled" && countryBlogs.value?.data?.docs) || [];
        const latestDocs =
          (latestBlogs.status === "fulfilled" && latestBlogs.value?.data?.docs) || [];

        // Step 3: Merge with priority, deduplicating by ID
        // Country/state pages: country > city > global
        // City/univ/locality pages: city > country > global
        const seen = new Set<number>();
        let fetchedBlogs: any[] = [];
        const isCountryOrState = ["country", "state"].includes(regionType || "");

        const primaryDocs = isCountryOrState ? countryDocs : cityDocs;
        const secondaryDocs = isCountryOrState ? cityDocs : countryDocs;

        primaryDocs.forEach((blog: any) => {
          if (!seen.has(blog.id) && fetchedBlogs.length < 10) {
            seen.add(blog.id);
            fetchedBlogs.push({ ...blog, _matchedByCity: !isCountryOrState });
          }
        });

        secondaryDocs.forEach((blog: any) => {
          if (!seen.has(blog.id) && fetchedBlogs.length < 10) {
            seen.add(blog.id);
            fetchedBlogs.push(blog);
          }
        });

        latestDocs.forEach((blog: any) => {
          if (!seen.has(blog.id) && fetchedBlogs.length < 10) {
            seen.add(blog.id);
            fetchedBlogs.push(blog);
          }
        });

        // Transform image URLs
        fetchedBlogs = fetchedBlogs.map((blog: any) => ({
          ...blog,
          mainImage: blog.mainImage
            ? { ...blog.mainImage, url: getBlogImageUrl(blog.mainImage.url) }
            : blog.mainImage,
        }));

        setBlogs(fetchedBlogs);
      } catch {
        // Silently fail — featured blogs are non-critical
      }
    };

    fetchBlogs();
  }, [resolvedCityName, regionType, countryName]);

  if (!blogs.length) return null;

  // Title: only show city or country name
  const hasCityBlogs = blogs.some((blog: any) => blog._matchedByCity);
  const isCountryOrStatePage = ["country", "state"].includes(regionType || "");
  let sectionTitle = "Latest Blogs";
  if (isCountryOrStatePage && countryName) {
    sectionTitle = `Featured Blogs on ${countryName}`;
  } else if (hasCityBlogs && resolvedCityName) {
    sectionTitle = `Featured Blogs on ${resolvedCityName}`;
  } else if (countryName) {
    sectionTitle = `Featured Blogs on ${countryName}`;
  }

  return (
    <div className={styles.mobileCardWrapper}>
      <FeaturedBlogSection
        blogs={blogs}
        basePath="/blog"
        title={sectionTitle}
        className={isCountryOrStatePage ? undefined : styles.searchFeaturedSection}
      />
    </div>
  );
};

export default wrapperHOC(memo(SearchFeaturedBlogs), {
  componentName: "SearchFeaturedBlogs-Search",
  showForChina: false,
});
